import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  TextProps,
} from "@react-pdf/renderer";
import { useCallback } from "react";
import { BracketMatch } from "../../api/Tournaments";

type Props = TextProps & {
  children: string;
};

const EmojiFreeText: React.FC<Props> = ({ children, ...props }) => {
  // Regex compatibile con React-PDF per rimuovere la maggior parte delle emoji
  const strippedText = children.replace(
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDD00-\uDDFF])/g,
    ""
  );

  return <Text {...props}>{strippedText}</Text>;
};

// ✅ Stili PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    padding: 20,
    fontSize: 11,
    fontFamily: "Helvetica",
  },
  header: {
    textAlign: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 10,
  },
  section: {
    marginBottom: 15,
    padding: 10,
    border: "1pt solid #ddd",
    borderRadius: 4,
  },
  gironeTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 6,
    textAlign: "center",
  },
  pair: {
    fontSize: 11,
    paddingVertical: 2,
    borderBottom: "0.5pt solid #eee",
    textAlign: "center",
  },
  giornata: {
    fontSize: 12,
    marginVertical: 4,
    textAlign: "center",
  },
  match: {
    fontSize: 11,
    marginVertical: 2,
    textAlign: "center",
  },
});

// "2026-08-06T15:30" -> "15:30" (prefisso orario per il PDF).
const pdfTime = (dt?: string): string => {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())} · `;
};

// ✅ Componente PDF
const TournamentPDF = ({
  tournament,
  brackets,
  parseGoldBracket,
  parseSilverBracket,
}: any) => {
  const isRiposoMatch = useCallback((fixture: BracketMatch[]) => {
    let restMatch: string = "";
    fixture.forEach((match) => {
      if (match.home !== "BYE") {
        //Controllo match.away
        if (match.away !== "BYE") {
          if (
            match.away.player1Name === "Riposo" ||
            match.away.player2Name === "Riposo"
          )
            restMatch = `${match.home.player1Name} / ${match.home.player2Name}`;
          else if (
            match.home.player1Name === "Riposo" ||
            match.home.player2Name === "Riposo"
          )
            restMatch = `${match.away.player1Name} / ${match.away.player2Name}`;
        }
      }
    });
    return restMatch;
  }, []);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Intestazione */}
        <View style={styles.header}>
          <Image
            style={{
              height: 100,
              alignSelf: "center",
              justifyContent: "center",
              alignItems: "center",
            }}
            src={require("../../assets/logo/logo.png")}
          />
          <EmojiFreeText style={styles.title}>{tournament.name}</EmojiFreeText>
          <Text style={styles.subtitle}>Tabelloni e Calendari</Text>
        </View>

        {/* Gironi */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Gironi</Text>
          {brackets.gironi?.map((girone: any) => (
            <View key={girone.name} style={{ marginBottom: 10 }}>
              <Text style={styles.gironeTitle}>Girone {girone.name}</Text>

              {/* Coppie */}
              {girone.pairs.length > 0 ? (
                girone.pairs.map((pair: any) => (
                  <Text key={pair.id} style={styles.pair}>
                    {pair.player1Name} / {pair.player2Name}
                  </Text>
                ))
              ) : (
                <Text style={styles.match}>Nessuna coppia</Text>
              )}

              {/* Calendario */}
              {girone.calendario?.map((fixture: any, i: number) => (
                <View key={i}>
                  <Text style={styles.giornata}>
                    {fixture.giornata}° Giornata
                  </Text>
                  {fixture.matches
                    .filter(
                      (m: any) => m.home.id !== "BYE" && m.away.id !== "BYE"
                    )
                    .map((m: any, idx: number) => (
                      <Text key={idx} style={styles.match}>
                        {pdfTime(m.dateTime)}
                        {m.home.player1Name} / {m.home.player2Name} {" - "}
                        {m.away.player1Name} / {m.away.player2Name}
                      </Text>
                    ))}
                  <Text style={styles.match}>
                    {isRiposoMatch(fixture.matches) === ""
                      ? ""
                      : `Riposa: ${isRiposoMatch(fixture.matches)}`}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>

        {/* Fase Finale Gold */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>
            Fase Finale {brackets.silverBracket ? "Gold" : ""}
          </Text>
          {parseGoldBracket.map((round: any, i: number) => (
            <View key={i}>
              <Text style={styles.giornata}>{round.title}</Text>
              {round.seeds.map((seed: any, idx: number) => (
                <Text key={idx} style={styles.match}>
                  {pdfTime(seed.dateTime)}
                  {seed.teams[0]?.name || "—"} vs {seed.teams[1]?.name || "—"}
                </Text>
              ))}
            </View>
          ))}
        </View>

        {/* Tabellone Silver */}
        {brackets.silverBracket && (
          <View style={styles.section}>
            <Text style={styles.subtitle}>Tabellone Silver</Text>
            {parseSilverBracket.map((round: any, i: number) => (
              <View key={i}>
                <Text style={styles.giornata}>{round.title}</Text>
                {round.seeds.map((seed: any, idx: number) => (
                  <Text key={idx} style={styles.match}>
                    {seed.teams[0]?.name || "—"} vs {seed.teams[1]?.name || "—"}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        )}
      </Page>
    </Document>
  );
};

export default TournamentPDF;
